// src/pages/HomeAlt.js — ELİTLİG Lig Merkezi (anasayfa)
//
// Görünüm "mor halı saha" tasarımıdır (HomeHub.css `hp-`): solda dönen
// manşet bandı, sağda sıradaki maç ve gol krallığı; altında sezonun sayı
// şeridi; son sonuçlar · haftanın maçları · puan durumu · haftanın enleri
// kartları; ardından canlı maçlar, istatistik liderleri, haftanın karması,
// sezon panosu ve bilgilendirme akışı.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { SiteSponsorBanner } from "../components/SiteSponsors";
import { Link, useNavigate } from "react-router-dom";

import { useData } from "./Cache";
import { useAltTheme } from "./useAltTheme";
import SiteHeader from "../components/layout/SiteHeader";
import { useAltScopedPageData } from "./useAltScopedPageData";

import { isMatchCountable, isMatchScheduledLike, parseScore, resolveLiveStatus, toBool } from "./AltHomeUtils";
import useLiveMatchFeed from "../hooks/useLiveMatchFeed";

import {
  buildTeamMaps,
  resolveTeamRecord,
} from "../utils/entityResolvers";
import api from "../api/api";
import WeeklyLineupCard from "../components/weeklyAwards/WeeklyLineupCard";
import useWeeklyAwardsSet from "../components/weeklyAwards/useWeeklyAwardsSet";
import HomeInfoFeed from "../components/home/HomeInfoFeed";
import defaultHeroImage from "../assets/field.jpg";

import "./HomeAlt.css";
import "./HomeHub.css";

/* ===================== Helpers ===================== */
const safeArr = (v) => (Array.isArray(v) ? v : []);
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
const strVal = (v, fallback = "") => {
  const s = String(v ?? "").trim();
  return s ? s : fallback;
};
const numVal = (v, fallback = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
};

const fmtDateShort = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short", weekday: "short" });
};

// Liste satırlarındaki tarih bloğu: { day: "8", mon: "Eki", label: "8 Eki" }
const fmtDayMonth = (raw) => {
  if (!raw) return { day: "", mon: "", label: "" };
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return { day: String(raw), mon: "", label: String(raw) };
  const mon = d.toLocaleDateString("tr-TR", { month: "short" });
  return { day: String(d.getDate()), mon, label: `${d.getDate()} ${mon}` };
};

const fmtTime = (raw) => {
  if (raw === undefined || raw === null) return "Saat bekleniyor";
  const s = String(raw).trim();
  if (!s) return "Saat bekleniyor";
  const p = s.split(":");
  if (p.length >= 2) return `${p[0].padStart(2, "0")}:${p[1].padStart(2, "0")}`;
  return s;
};

const getMatchTs = (m) => {
  const d = new Date(m?.date || 0);
  const t = String(m?.time || "00:00").split(":");
  const hh = Number(t[0] || 0);
  const mm = Number(t[1] || 0);
  if (Number.isNaN(d.getTime())) return Number.NEGATIVE_INFINITY;
  d.setHours(Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
  return d.getTime();
};

const teamKeyOf = (teamName, teamId) => {
  const normalizedName = String(teamName || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();
  if (teamId !== undefined && teamId !== null && String(teamId).trim() !== "") {
    return `id:${String(teamId).trim()}`;
  }
  return `name:${normalizedName}`;
};

// ✅ CANLI tespiti: mac_durumu=canli öncelikli
const isLiveMatch = (m) => {
  if (!m) return false;

  const md = String(m.mac_durumu || "").toLowerCase();
  if (md === "canli") return true;

  if (m.is_live === true) return true;

  const s = String(m.live_status || m.status || "").toLowerCase();
  if (["live", "canli", "inplay"].includes(s)) return true;

  return resolveLiveStatus(m).isLive;
};

const isFinished = (m) => {
  if (!m) return false;
  if (!isMatchCountable(m)) return false;
  const h = parseScore(m.first_team_score);
  const a = parseScore(m.second_team_score);
  if (h !== null && a !== null) return true;
  const matchStatus = String(m?.mac_durumu || m?.match_status || m?.status || "").toLocaleLowerCase("tr-TR");
  if (matchStatus.includes("yayinlanmis") || matchStatus.includes("bitti") || matchStatus.includes("finished")) return true;
  return false;
};

/* ===================== Player statistics endpoint ===================== */
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getAssetUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  const baseUrl = String(api.defaults.baseURL || "").replace(/\/+$/, "");
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return baseUrl ? `${baseUrl}${path}` : path;
};

const normalizeSearchText = (value) =>
  String(value || "").toLocaleLowerCase("tr-TR").trim();

const parseActiveStatus = (item = {}) => {
  const rawStatus =
    item.playerActive ?? item.isActive ?? item.active ??
    item.aktif_mi ?? item.aktif ?? item.playerStatus ?? item.status;
  if (rawStatus === undefined || rawStatus === null || rawStatus === "") return true;
  if (typeof rawStatus === "boolean") return rawStatus;
  if (typeof rawStatus === "number") return rawStatus === 1;
  const s = normalizeSearchText(rawStatus);
  if (["0", "false", "pasif", "inactive", "disabled", "silindi", "deleted"].includes(s)) return false;
  return true;
};

const getBackendRank = (item = {}) => {
  const rank = item.rank ?? item.ranking ?? item.positionRank ?? item.listRank ?? item.sira ?? item.sıra;
  const numericRank = Number(rank);
  return Number.isInteger(numericRank) && numericRank > 0 ? numericRank : null;
};

const normalizePlayer = (item = {}, fallbackRank = 0) => {
  const yellowCards = toNumber(item.yellowCards);
  const redCards = toNumber(item.redCards);
  return {
    id: item.playerId ?? item.oyuncuId ?? item.oyuncu_id ?? item.id ?? null,
    name: item.playerName ?? item.oyuncuAdi ?? item.oyuncu_adi ?? item.name ?? "",
    img: getAssetUrl(item.playerImage ?? item.oyuncuFotografi ?? item.oyuncu_fotografi ?? item.image),
    isActive: parseActiveStatus(item),
    teamId: item.teamId ?? item.takimId ?? item.takim_id ?? null,
    teamName: item.teamName ?? item.takimAdi ?? item.takim_adi ?? "-",
    teamLogo: getAssetUrl(item.teamLogo ?? item.takimLogo ?? item.takim_logo),
    position: item.position ?? item.pozisyon ?? "",
    points: toNumber(item.totalPoints ?? item.toplamPuan ?? item.toplam_puan),
    goals: toNumber(item.totalGoals ?? item.toplamGol ?? item.toplam_gol),
    assists: toNumber(item.assists ?? item.asist),
    matches: toNumber(item.matchesPlayed ?? item.oynadigiMacSayisi ?? item.oynadigi_mac_sayisi),
    yellowCards,
    redCards,
    totalCards: toNumber(item.totalCards ?? item.toplamKart ?? item.toplam_kart ?? (yellowCards + redCards)),
    goalsPerMatch: toNumber(item.goalsPerMatch ?? item.golOrtalamasi ?? item.gol_ortalamasi),
    pointsPerMatch: toNumber(item.pointsPerMatch ?? item.puanOrtalamasi ?? item.puan_ortalamasi),
    rank: getBackendRank(item) ?? fallbackRank,
  };
};

/* Anasayfa sekmeleri -> backend sort anahtarları */
const PLAYER_TAB_SORT = {
  mvp: "mostValuable",
  goals: "topScorers",
  all: "mostMatches",
};

const PLAYER_FETCH_LIMIT = 10;

/* ===================== Small view components ===================== */
/* Sıradaki maç geri sayımı. Saniyelik tik yalnızca bu küçük bileşeni
   yeniler; eskiden HomeAlt'ın tamamını her saniye yeniden çiziyordu ve
   içinde tanımlı canlı maç kartları her saniye yeniden kurulup (socket
   kapat / aç + anlık görüntü isteği) sunucuyu boğuyordu. */
function Countdown({ targetTs }) {
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const diff = Math.max(0, targetTs - nowTs);
  const dd = Math.floor(diff / 86400000);
  const hh = Math.floor((diff % 86400000) / 3600000);
  const mm = Math.floor((diff % 3600000) / 60000);
  const ss = Math.floor((diff % 60000) / 1000);
  const seg = (v, l) => (
    <span className="lm-spot__count-seg">
      <strong>{String(v).padStart(2, "0")}</strong>
      <em>{l}</em>
    </span>
  );
  return (
    <div className="lm-spot__count" aria-label="Maça kalan süre">
      {seg(dd, "GÜN")}
      <span className="lm-spot__count-sep">:</span>
      {seg(hh, "SAAT")}
      <span className="lm-spot__count-sep">:</span>
      {seg(mm, "DK")}
      <span className="lm-spot__count-sep">:</span>
      {seg(ss, "SN")}
    </div>
  );
}

/* Canlı maç kartı. Bileşen HomeAlt'ın DIŞINDA tanımlı: içeride tanımlansaydı
   her çizimde yeni bir bileşen tipi olur, React kartı söküp yeniden kurar ve
   canlı kanal (socket + anlık görüntü) her seferinde yeniden açılırdı. */
const LiveMatchCard = React.memo(function LiveMatchCard({ match, to, homeLogo, awayLogo }) {
  const liveFeed = useLiveMatchFeed(match.id);

  const homeScore = Number.isFinite(Number(liveFeed?.homeScore))
    ? Number(liveFeed.homeScore)
    : (n(match.first_team_score) ?? 0);

  const awayScore = Number.isFinite(Number(liveFeed?.awayScore))
    ? Number(liveFeed.awayScore)
    : (n(match.second_team_score) ?? 0);

  const liveLabel = liveFeed?.minuteText || liveFeed?.clockText || match.live_time || "CANLI";

  return (
    <li>
      <Link to={to} className="lm-live-card">
        <div className="lm-live-card__top">
          <span className="lm-live-dot" />
          <span className="lm-live-card__minute">{liveLabel}</span>
          <span className="lm-live-card__venue">{match.pitch_name || match.field_name || match.match_field || ""}</span>
        </div>
        <div className="lm-live-card__row">
          <span className="lm-live-card__team">
            <TeamMark name={match.first_team_name} logo={homeLogo} size={22} />
            {match.first_team_name}
          </span>
          <strong className="lm-live-card__score">{homeScore}</strong>
        </div>
        <div className="lm-live-card__row">
          <span className="lm-live-card__team">
            <TeamMark name={match.second_team_name} logo={awayLogo} size={22} />
            {match.second_team_name}
          </span>
          <strong className="lm-live-card__score">{awayScore}</strong>
        </div>
      </Link>
    </li>
  );
});

function TeamMark({ name, logo, size = 26 }) {
  if (logo) {
    return <img className="lm-teammark" src={logo} alt="" width={size} height={size} loading="lazy" />;
  }
  return <span className="lm-teammark lm-teammark--ph">{String(name || "?").charAt(0)}</span>;
}

/* Küçük çizgi ikonlar (kart başlıkları ve manşet) */
const Ico = ({ d, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICONS = {
  ball: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7.5l3.5 2.5-1.3 4h-4.4l-1.3-4zM12 3v4.5M15.5 10l4.3-1.4M14.2 14l2.6 3.7M9.8 14l-2.6 3.7M8.5 10 4.2 8.6",
  cal: "M4.5 5.5h15v14h-15zM8 3.5v4M16 3.5v4M4.5 10h15M8.5 14h2M13.5 14h2M8.5 17h2",
  trophy: "M7 4h10v4a5 5 0 0 1-10 0V4ZM7 5H4v1a4 4 0 0 0 3 3.9M17 5h3v1a4 4 0 0 1-3 3.9M12 13v4m-4 4h8m-6.5-4h5",
  star: "m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z",
  target: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM12 12.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Z",
  users: "M16 20v-1.5a4.5 4.5 0 0 0-4.5-4.5h-3A4.5 4.5 0 0 0 4 18.5V20m6-9a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7-1a3 3 0 0 0 0-6m3 16v-1.5a4 4 0 0 0-3-3.9",
  pitch: "M3 5.5h18v13H3zM12 5.5v13M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM3 9h3v6H3M21 9h-3v6h3",
  chart: "M5 20v-6M10 20V9M15 20v-9M20 20V5M3 20h18",
  live: "M4 12h3l2.5-6 5 12 2.5-6h3",
  pulse: "M3 12h4l3-8 4 16 3-8h4",
};
const CalIcon = () => <Ico d={ICONS.cal} size={18} />;
const PinIcon = () => <Ico d="M12 21s7-5.1 7-11a7 7 0 1 0-14 0c0 5.9 7 11 7 11ZM12 12.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2Z" size={18} />;
const ArrowIcon = () => <Ico d="M5 12h14M13 6l6 6-6 6" size={18} />;

const WEEKLY_LABELS = {
  WEEKLY_PLAYER: "Haftanın Oyuncusu",
  WEEKLY_GOALKEEPER: "Haftanın Kalecisi",
  WEEKLY_DEFENDER: "Haftanın Defansı",
  WEEKLY_MIDFIELDER: "Haftanın Orta Sahası",
  WEEKLY_FORWARD: "Haftanın Forveti",
  WEEKLY_FAIR_PLAY: "Haftanın Centilmeni",
};

/* ===================== Component ===================== */
export default function HomeAlt() {
  const navigate = useNavigate();
  const { toggleTheme, themeClassName } = useAltTheme();

  const scope = useAltScopedPageData({ pageKey: "anasayfa" });

  const cityId = scope.selectedCity;
  const leagueId = scope.selectedLeague;
  const seasonId = scope.selectedSeason;

  // Haftanın Enleri (bireysel ödül şeridi + haftanın karması sahası) tek fetch.
  // Sezon bazlı: seçili sezonun seti gösterilir.
  const { set: weeklySet } = useWeeklyAwardsSet(cityId, { seasonId, leagueId });

  const data = useData();
  const matches = safeArr(data?.matches);
  const teams = safeArr(data?.teams);
  const standingsRows = safeArr(data?.standings);

  const loading = Boolean(scope.loading || data?.loading);
  const error = scope.error || data?.error || "";

  /* ---------- UI tabs ---------- */
  const [playerTab, setPlayerTab] = useState("mvp");
  const [weeklyIdx, setWeeklyIdx] = useState(0);

  /* ---------- Player statistics ---------- */
  const [playerRows, setPlayerRows] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);

  useEffect(() => {
    if (!scope.hasScopedSelection) {
      setPlayerRows([]);
      return undefined;
    }

    const controller = new AbortController();
    const sort = PLAYER_TAB_SORT[playerTab] || "mostValuable";

    const params = {
      cityId: cityId || undefined,
      leagueId: leagueId || undefined,
      seasonId: seasonId || undefined,
      sort,
      limit: PLAYER_FETCH_LIMIT,
      offset: 0,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === null || params[key] === "") {
        delete params[key];
      }
    });

    setPlayersLoading(true);

    api
      .get("/api/players/statistics", { params, signal: controller.signal })
      .then((response) => {
        const payload = response?.data || {};
        const rows = Array.isArray(payload.players) ? payload.players : [];
        const offset = toNumber(payload.pagination?.offset ?? 0);
        const normalized = rows
          .map((item, index) => normalizePlayer(item, offset + index + 1))
          .filter((player) => player.isActive && player.id != null);
        setPlayerRows(normalized);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setPlayerRows([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPlayersLoading(false);
      });

    return () => controller.abort();
  }, [scope.hasScopedSelection, cityId, leagueId, seasonId, playerTab]);

  /* ---------- Gol krallığı (ilk 5) ---------- */
  const [topScorers, setTopScorers] = useState([]);

  useEffect(() => {
    if (!scope.hasScopedSelection) {
      setTopScorers([]);
      return undefined;
    }

    const controller = new AbortController();
    const params = {
      cityId: cityId || undefined,
      leagueId: leagueId || undefined,
      seasonId: seasonId || undefined,
      sort: "topScorers",
      limit: 5,
      offset: 0,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === null || params[key] === "") {
        delete params[key];
      }
    });

    api
      .get("/api/players/statistics", { params, signal: controller.signal })
      .then((response) => {
        const payload = response?.data || {};
        const rows = Array.isArray(payload.players) ? payload.players : [];
        setTopScorers(rows.map((row, i) => normalizePlayer(row, i + 1)).filter((p) => p.isActive && p.id != null));
      })
      .catch(() => {
        if (!controller.signal.aborted) setTopScorers([]);
      });

    return () => controller.abort();
  }, [scope.hasScopedSelection, cityId, leagueId, seasonId]);

  /* ---------- Indexes ---------- */
  const teamMaps = useMemo(() => buildTeamMaps(teams), [teams]);

  /* ---------- Navigation helpers ---------- */
  const scopedPrefix = `/sehir/${encodeURIComponent(cityId)}/lig/${encodeURIComponent(
    leagueId
  )}/sezon/${encodeURIComponent(seasonId)}`;
  const matchLink = (matchId) => `${scopedPrefix}/mac/${encodeURIComponent(matchId)}`;
  const teamLink = (teamId) => `${scopedPrefix}/takim/${encodeURIComponent(teamId)}`;
  const playerLink = (playerId) => `${scopedPrefix}/oyuncu/${encodeURIComponent(playerId)}`;

  const toMatchesPage = useCallback(() => {
    if (!cityId || !leagueId || !seasonId) return;
    navigate(`${scopedPrefix}/maclar`);
  }, [cityId, leagueId, seasonId, navigate, scopedPrefix]);

  const toStandingsPage = useCallback(() => {
    if (!cityId || !leagueId || !seasonId) return;
    navigate(`${scopedPrefix}/puan-durumu`);
  }, [cityId, leagueId, seasonId, navigate, scopedPrefix]);

  const toPlayersPage = useCallback(() => {
    if (!cityId || !leagueId || !seasonId) return;
    navigate(`${scopedPrefix}/oyuncular`);
  }, [cityId, leagueId, seasonId, navigate, scopedPrefix]);

  /* ---------- Derived match lists ---------- */
  const sortedMatches = useMemo(() => {
    return [...matches].sort((a, b) => getMatchTs(b) - getMatchTs(a));
  }, [matches]);

  const liveMatches = useMemo(() => {
    return sortedMatches.filter((m) => isLiveMatch(m)).slice(0, 8);
  }, [sortedMatches]);

  const liveMatchIds = useMemo(
    () => new Set(liveMatches.map((m) => String(m.id))),
    [liveMatches]
  );

  const finishedMatches = useMemo(() => {
    return sortedMatches
      .filter((m) => isFinished(m))
      .filter((m) => !isLiveMatch(m))
      .slice(0, 12);
  }, [sortedMatches]);

  const fixtures = useMemo(() => {
    return [...matches]
      .filter((m) => toBool(m?.is_it_fixture) || isMatchScheduledLike(m))
      .filter((m) => !isLiveMatch(m))
      .sort((a, b) => getMatchTs(a) - getMatchTs(b))
      .slice(0, 12);
  }, [matches]);

  /* ---------- Manşet slaytları ---------- */
  const heroSlides = useMemo(() => {
    const slides = [];
    liveMatches.slice(0, 2).forEach((m) => slides.push({ type: "live", m }));
    fixtures.slice(0, 3).forEach((m) => slides.push({ type: "next", m }));
    finishedMatches.slice(0, 5).forEach((m) => slides.push({ type: "last", m }));
    return slides.slice(0, 10);
  }, [liveMatches, fixtures, finishedMatches]);

  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    setSlideIdx(0);
  }, [heroSlides.length]);

  useEffect(() => {
    if (heroSlides.length < 2) return undefined;
    const timer = setInterval(() => {
      setSlideIdx((i) => (i + 1) % heroSlides.length);
    }, 6500);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  /* ---------- Sıradaki maç geri sayımı ---------- */

  /* ---------- Standings (computed) ---------- */
  const standings = useMemo(() => {
    if (!scope.hasScopedSelection) return [];

    const toScopeId = (value) => String(value ?? "").trim();
    const rowMatchesScope = (row) => {
      if (!row) return false;
      const scopedCheck = (keys, expected) => {
        const expectedId = toScopeId(expected);
        if (!expectedId) return true;

        const candidates = keys
          .map((key) => row?.[key])
          .map((value) => toScopeId(value))
          .filter(Boolean);

        if (!candidates.length) return true;
        return candidates.includes(expectedId);
      };

      return (
        scopedCheck(["cityId", "city_id", "city"], cityId) &&
        scopedCheck(["leagueId", "league_id", "league"], leagueId) &&
        scopedCheck(["seasonId", "season_id", "season"], seasonId)
      );
    };

    const normalizedStandings = standingsRows
      .filter((row) => rowMatchesScope(row))
      .map((row) => {
        const teamName = strVal(row.teamName ?? row.team_name ?? row.name ?? row.team, "");
        if (!teamName) return null;

        const teamId = row.teamId ?? row.team_id ?? row.id ?? null;
        const resolvedTeam = resolveTeamRecord(teamId, teamName, teamMaps);

        const wins = numVal(row.wins ?? row.team_wins ?? row.galibiyet, 0);
        const draws = numVal(row.draws ?? row.team_draws ?? row.beraberlik, 0);
        const goalsFor = numVal(row.goalsFor ?? row.gf ?? row.goals_scored ?? row.atilan_gol, 0);
        const goalsAgainst = numVal(row.goalsAgainst ?? row.ga ?? row.goals_conceded ?? row.yenilen_gol, 0);
        const manualPoints = numVal(row.manualPoints ?? row.manual_points ?? row.team_points ?? row.adjustment_points, 0);
        const basePoints = numVal(row.basePoints ?? row.base_points ?? row.points ?? row.puan, wins * 3 + draws);
        const totalRaw = numVal(row.totalPoints ?? row.total_points ?? row.puan_toplam, basePoints + manualPoints);
        const totalPoints = Math.max(totalRaw, basePoints + manualPoints);

        return {
          key: teamId != null ? `id:${teamId}` : `nm:${teamName}`,
          id: teamId ?? resolvedTeam?.id ?? null,
          name: teamName,
          logo:
            strVal(row.logo ?? row.team_logo ?? row.logo_url, "") ||
            strVal(resolvedTeam?.logo, "") ||
            strVal(resolvedTeam?.team_logo, "") ||
            strVal(resolvedTeam?.logo_url, ""),
          played: numVal(row.played ?? row.played_matches ?? row.matches_played ?? row.oynanan_mac, 0),
          wins,
          draws,
          losses: numVal(row.losses ?? row.team_losses ?? row.maglubiyet, 0),
          goalsFor,
          goalsAgainst,
          goalDiff: numVal(row.gd ?? row.goalDiff ?? row.average, goalsFor - goalsAgainst),
          // Güç dengesi sezonlarında backend display_points balance toplamını verir;
          // standart sezonlarda display_points = total_points -> görsel değişmez.
          points: numVal(row.display_points ?? row.displayPoints, totalPoints),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDiff !== a.goalDiff) return b.goalDiff - a.goalDiff;
        if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
        return a.name.localeCompare(b.name, "tr", { sensitivity: "base" });
      });

    if (normalizedStandings.length) return normalizedStandings.slice(0, 25);

    // Endpoint verisi gelmezse, eski akışla hesaplanan tabloya fallback.
    const table = new Map();

    const ensureTeam = (teamName, teamId) => {
      if (!teamName) return null;
      const key = teamKeyOf(teamName, teamId);
      if (!table.has(key)) {
        const ref = resolveTeamRecord(teamId, teamName, teamMaps);
        table.set(key, {
          key: teamId != null ? `id:${teamId}` : `nm:${teamName}`,
          id: teamId ?? ref?.id ?? null,
          name: teamName,
          logo:
            strVal(ref?.logo, "") ||
            strVal(ref?.team_logo, "") ||
            strVal(ref?.logo_url, ""),
          played: 0,
          wins: 0,
          draws: 0,
          losses: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDiff: 0,
          points: 0,
        });
      }
      return table.get(key);
    };

    matches.filter((match) => isMatchCountable(match)).forEach((match) => {
      const matchId = String(match?.id ?? "");
      const isLive = matchId && liveMatchIds.has(matchId);
      if (isLive) return;

      const homeScore = parseScore(match?.first_team_score);
      const awayScore = parseScore(match?.second_team_score);
      if (homeScore === null || awayScore === null) return;

      const home = ensureTeam(match?.first_team_name, match?.first_team_id);
      const away = ensureTeam(match?.second_team_name, match?.second_team_id);
      if (!home || !away) return;

      if (!home.logo && match?.first_team_logo) home.logo = match.first_team_logo;
      if (!away.logo && match?.second_team_logo) away.logo = match.second_team_logo;

      home.played += 1;
      away.played += 1;
      home.goalsFor += homeScore;
      home.goalsAgainst += awayScore;
      away.goalsFor += awayScore;
      away.goalsAgainst += homeScore;

      if (homeScore > awayScore) {
        home.wins += 1; home.points += 3; away.losses += 1;
      } else if (homeScore < awayScore) {
        away.wins += 1; away.points += 3; home.losses += 1;
      } else {
        home.draws += 1; away.draws += 1; home.points += 1; away.points += 1;
      }
    });

    table.forEach((entry) => {
      const ref = resolveTeamRecord(entry.id, entry.name, teamMaps);
      const manualPoints = numVal(ref?.team_points, 0);
      entry.points += manualPoints;
      entry.goalDiff = entry.goalsFor - entry.goalsAgainst;
    });

    return Array.from(table.values())
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDiff !== a.goalDiff) return b.goalDiff - a.goalDiff;
        if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
        return a.name.localeCompare(b.name, "tr", { sensitivity: "base" });
      })
      .slice(0, 25);
  }, [
    matches,
    scope.hasScopedSelection,
    standingsRows,
    cityId,
    leagueId,
    seasonId,
    teamMaps,
    liveMatchIds,
  ]);

  /* ---------- League pulse & insights ---------- */
  const leaguePulse = useMemo(() => {
    if (!standings.length) return null;
    const teamsCount = standings.length;
    const playedTotal = Math.round(standings.reduce((acc, t) => acc + t.played, 0) / 2);
    const goalsTotal = standings.reduce((acc, t) => acc + t.goalsFor, 0);
    const goalsPerMatch = playedTotal > 0 ? (goalsTotal / playedTotal).toFixed(1) : "0.0";
    return { teamsCount, playedTotal, goalsTotal, goalsPerMatch };
  }, [standings]);

  const insights = useMemo(() => {
    if (!standings.length) return [];
    const played = standings.filter((t) => t.played > 0);
    if (!played.length) return [];

    const leader = standings[0];
    const bestAttack = [...played].sort((a, b) => b.goalsFor - a.goalsFor)[0];
    const bestDefense = [...played].sort((a, b) => a.goalsAgainst - b.goalsAgainst)[0];
    const bestDiff = [...played].sort((a, b) => b.goalDiff - a.goalDiff)[0];

    return [
      { key: "leader", label: "Lider", team: leader, value: `${leader.points} puan` },
      { key: "attack", label: "En Golcü Takım", team: bestAttack, value: `${bestAttack.goalsFor} gol` },
      { key: "defense", label: "En İyi Savunma", team: bestDefense, value: `${bestDefense.goalsAgainst} gol yedi` },
      { key: "diff", label: "Averaj Lideri", team: bestDiff, value: `${bestDiff.goalDiff > 0 ? "+" : ""}${bestDiff.goalDiff} averaj` },
    ];
  }, [standings]);

  /* ---------- Winning streak ---------- */
  const winStreakTeam = useMemo(() => {
    if (!standings.length || !matches.length) return null;

    const teamMatches = new Map();
    [...matches]
      .filter((m) => isFinished(m) && !isLiveMatch(m))
      .sort((a, b) => getMatchTs(b) - getMatchTs(a))
      .forEach((m) => {
        const hScore = parseScore(m.first_team_score);
        const aScore = parseScore(m.second_team_score);
        if (hScore === null || aScore === null) return;

        const addResult = (name, id, won) => {
          const key = teamKeyOf(name, id);
          if (!teamMatches.has(key)) teamMatches.set(key, { name, id, results: [] });
          teamMatches.get(key).results.push(won);
        };

        addResult(m.first_team_name, m.first_team_id, hScore > aScore);
        addResult(m.second_team_name, m.second_team_id, aScore > hScore);
      });

    let best = null;
    teamMatches.forEach((entry) => {
      let streak = 0;
      for (const won of entry.results) {
        if (won) streak++;
        else break;
      }
      if (streak > 0 && (!best || streak > best.streak)) {
        const standing = standings.find((t) => {
          if (entry.id != null && t.id != null) return String(t.id) === String(entry.id);
          return t.name === entry.name;
        });
        best = { name: entry.name, id: entry.id, streak, logo: standing?.logo || "" };
      }
    });

    return best;
  }, [standings, matches]);

  /* ---------- Manşet slaytı ---------- */
  const renderSlide = (slide, idx) => {
    const m = slide.m;
    const firstTeam = resolveTeamRecord(m.first_team_id, m.first_team_name, teamMaps);
    const secondTeam = resolveTeamRecord(m.second_team_id, m.second_team_name, teamMaps);
    const homeLogo = firstTeam?.logo || m.first_team_logo || "";
    const awayLogo = secondTeam?.logo || m.second_team_logo || "";
    const image = m.match_picture || defaultHeroImage;

    const homeScore = n(m.first_team_score);
    const awayScore = n(m.second_team_score);
    const showScore = slide.type !== "next" && homeScore !== null && awayScore !== null;

    /* Manşet metni — manuel manşet varsa onu göster, yoksa otomatik üret */
    const home = m.first_team_name || "";
    const away = m.second_team_name || "";
    const manualHeadline = m.headline || m.manset || m.match_headline || "";

    let headline;
    if (manualHeadline.trim()) {
      headline = manualHeadline.trim();
    } else {
      const pick = (arr) => arr[Math.abs((m.id ? String(m.id).split("").reduce((a, c) => a + c.charCodeAt(0), 0) : 0)) % arr.length];
      const totalGoals = (homeScore ?? 0) + (awayScore ?? 0);
      const goalDiff = Math.abs((homeScore ?? 0) - (awayScore ?? 0));
      const winner = homeScore > awayScore ? home : away;
      const loser = homeScore > awayScore ? away : home;
      const winnerScore = homeScore > awayScore ? homeScore : awayScore;
      const loserScore = homeScore > awayScore ? awayScore : homeScore;

      if (slide.type === "live") {
        headline = pick([
          <><em>{home}</em> ile <em>{away}</em> sahada — heyecan sürüyor</>,
          <>Sahada dev mücadele: <em>{home}</em> – <em>{away}</em></>,
          <><em>{home}</em> – <em>{away}</em> maçı devam ediyor</>,
          <>Canlı: <em>{home}</em> ve <em>{away}</em> karşı karşıya</>,
          <>Saha kızıştı! <em>{home}</em> – <em>{away}</em></>,
          <>Maç başladı: <em>{home}</em> ile <em>{away}</em> kozlarını paylaşıyor</>,
        ]);
      } else if (slide.type === "next") {
        headline = pick([
          <>Haftanın randevusu: <em>{home}</em> – <em>{away}</em></>,
          <><em>{home}</em>, <em>{away}</em> ile kapışmaya hazırlanıyor</>,
          <>Büyük maç yaklaşıyor: <em>{home}</em> – <em>{away}</em></>,
          <>Gözler bu maçta: <em>{home}</em> vs <em>{away}</em></>,
          <><em>{home}</em> – <em>{away}</em> karşılaşması için geri sayım başladı</>,
          <>Sıradaki randevu: <em>{home}</em> ile <em>{away}</em></>,
        ]);
      } else if (showScore && homeScore !== awayScore && totalGoals >= 6) {
        headline = pick([
          <>Gol yağmuru! <em>{winner}</em>, <em>{loser}</em>'i {winnerScore}-{loserScore} mağlup etti</>,
          <>{totalGoals} gollük şölen: <em>{winner}</em> sahadan galip ayrıldı</>,
          <>Gol düellosu <em>{winner}</em>'in! Skor: {winnerScore}-{loserScore}</>,
          <>Çılgın maçın kazananı <em>{winner}</em>: {winnerScore}-{loserScore}</>,
        ]);
      } else if (showScore && homeScore !== awayScore && goalDiff >= 4) {
        headline = pick([
          <><em>{winner}</em>'den <em>{loser}</em>'e gol şov: {winnerScore}-{loserScore}</>,
          <>Farklı galibiyet! <em>{winner}</em>, <em>{loser}</em>'i dağıttı</>,
          <><em>{winner}</em>, <em>{loser}</em>'i sahadan sildi: {winnerScore}-{loserScore}</>,
          <>Ezici üstünlük! <em>{winner}</em> {goalDiff} farkla kazandı</>,
        ]);
      } else if (showScore && homeScore !== awayScore && loserScore === 0) {
        headline = pick([
          <><em>{winner}</em>, <em>{loser}</em>'e geçit vermedi: {winnerScore}-0</>,
          <>Kalesini gole kapatan <em>{winner}</em>, sahadan {winnerScore}-0 galip ayrıldı</>,
          <><em>{loser}</em> gol atamadı, <em>{winner}</em> güldü</>,
          <>Clean sheet! <em>{winner}</em> {winnerScore}-0 kazandı</>,
        ]);
      } else if (showScore && homeScore !== awayScore && goalDiff === 1) {
        headline = pick([
          <><em>{winner}</em>, <em>{loser}</em>'i son anda devirdi</>,
          <>Nefes kesen maç <em>{winner}</em>'in: {winnerScore}-{loserScore}</>,
          <>Kritik galibiyet! <em>{winner}</em> tek farkla kazandı</>,
          <>Zorlu maçtan <em>{winner}</em> galip çıktı: {winnerScore}-{loserScore}</>,
          <><em>{winner}</em>, <em>{loser}</em> karşısında 3 puanı kaptı</>,
          <>Maçın kazananı <em>{winner}</em>: {winnerScore}-{loserScore}</>,
        ]);
      } else if (showScore && homeScore !== awayScore) {
        headline = pick([
          <><em>{winner}</em>, <em>{loser}</em> karşısında güldü</>,
          <><em>{winner}</em> sahadan galip ayrıldı: {winnerScore}-{loserScore}</>,
          <><em>{loser}</em> direnemedi, <em>{winner}</em> kazandı</>,
          <>Galip <em>{winner}</em>! Skor: {winnerScore}-{loserScore}</>,
          <><em>{winner}</em>, {winnerScore}-{loserScore}'lık skorla 3 puanın sahibi</>,
          <><em>{winner}</em> rahat kazandı: {winnerScore}-{loserScore}</>,
        ]);
      } else if (showScore && homeScore === 0 && awayScore === 0) {
        headline = pick([
          <><em>{home}</em> ile <em>{away}</em> golsüz berabere kaldı</>,
          <>Goller sustu: <em>{home}</em> 0 – 0 <em>{away}</em></>,
          <><em>{home}</em> – <em>{away}</em> maçında kale gol görmedi</>,
          <>Golsüz kapanış: <em>{home}</em> ve <em>{away}</em> birer puan aldı</>,
        ]);
      } else if (showScore && homeScore === awayScore) {
        headline = pick([
          <><em>{home}</em> ile <em>{away}</em> yenişemedi</>,
          <>{homeScore}-{awayScore}'lık beraberlik: <em>{home}</em> – <em>{away}</em></>,
          <>Dostluk kazandı: <em>{home}</em> ve <em>{away}</em> puanları paylaştı</>,
          <>Eşitlik bozulmadı! <em>{home}</em> {homeScore} – {awayScore} <em>{away}</em></>,
          <>Her iki takım da galip gelemedi: <em>{home}</em> {homeScore}-{awayScore} <em>{away}</em></>,
        ]);
      } else {
        headline = <><em>{home}</em> – <em>{away}</em></>;
      }
    }

    const venue = m.pitch_name || m.field_name || m.match_field || "";
    const eyebrow = slide.type === "live" ? "Canlı maç" : slide.type === "next" ? "Sıradaki maç" : "Son maç";

    return (
      <Link
        key={`${m.id}-${idx}`}
        to={matchLink(m.id)}
        className={`hp-slide ${idx === slideIdx ? "is-active" : ""}`}
        aria-hidden={idx !== slideIdx}
        tabIndex={idx === slideIdx ? 0 : -1}
      >
        <div className="hp-slide__bg" style={{ backgroundImage: `url(${image})` }} />
        <div className="hp-slide__scrim" />
        <span className="hp-slide__stripes" aria-hidden="true" />

        <div className="hp-slide__content">
          <p className="hp-slide__eyebrow">
            {slide.type === "live" && <span className="hp-live-dot" />}
            {scope.scopeLabel ? `${scope.scopeLabel} · ` : ""}{eyebrow}
          </p>
          <h2 className="hp-slide__headline">{headline}</h2>
          <span className="hp-slide__rule" aria-hidden="true" />

          <div className="hp-slide__meta">
            <span><CalIcon /> {fmtDateShort(m.date)} · {fmtTime(m.time)}</span>
            {venue && <><span className="hp-slide__sep" aria-hidden="true" /><span><PinIcon /> {venue}</span></>}
          </div>

          <div className="hp-slide__board">
            <span className="hp-slide__team">
              {homeLogo ? <img src={homeLogo} alt="" loading="lazy" /> : <span className="hp-ph">{home.charAt(0) || "?"}</span>}
              {home}
            </span>
            <span className={`hp-slide__score${showScore ? "" : " is-vs"}`}>{showScore ? `${homeScore} - ${awayScore}` : "VS"}</span>
            <span className="hp-slide__team">
              {awayLogo ? <img src={awayLogo} alt="" loading="lazy" /> : <span className="hp-ph">{away.charAt(0) || "?"}</span>}
              {away}
            </span>
          </div>

          <span className="hp-pill-btn">{slide.type === "live" ? "Canlı takip et" : "Maç detayını gör"} <ArrowIcon /></span>
        </div>
      </Link>
    );
  };

  /* ---------- Spot içerikleri (sağ sütun) ---------- */
  const spotlightMatch = liveMatches[0] || fixtures[0] || finishedMatches[0] || null;
  const spotlightIsLive = Boolean(liveMatches[0]);
  const spotlightIsFinished = !liveMatches[0] && !fixtures[0] && Boolean(finishedMatches[0]);
  const starPlayer = playerRows[0] || null;
  const leaderTeam = standings[0] || null;

  /* ---------- Header props ---------- */
  const headerProps = {
    cityOptions: scope.cityOptions,
    leagueOptions: scope.leagueOptions,
    seasonOptions: scope.seasonOptions,
    selectedCity: scope.selectedCity,
    selectedLeague: scope.selectedLeague,
    selectedSeason: scope.selectedSeason,
    onCityChange: scope.onCityChange,
    onLeagueChange: scope.onLeagueChange,
    onSeasonChange: scope.onSeasonChange,
    scopeLabel: scope.scopeLabel,
    showFilters: true,
    onToggleTheme: toggleTheme,
    toneClasses: { accent: "#6d28d9" },
  };

  if (loading) {
    return (
      <div className={`alt-home-page ${themeClassName}`}>
        <SiteHeader {...headerProps} />
        <main className="alt-home">
          <div className="alt-shell">
            <div className="alt-state">Veriler yükleniyor…</div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`alt-home-page ${themeClassName}`}>
        <SiteHeader {...headerProps} />
        <main className="alt-home">
          <div className="alt-shell">
            <div className="alt-state alt-state--error">Hata: {error}</div>
          </div>
        </main>
      </div>
    );
  }

  const playerTabMeta = {
    mvp: { label: "En Değerli", unit: "PUAN" },
    goals: { label: "Gol", unit: "GOL" },
    all: { label: "Maç", unit: "MAÇ" },
  };

  const logoOf = (id, name, fallback) => resolveTeamRecord(id, name, teamMaps)?.logo || fallback || "";
  const weeklyItems = (() => {
    const items = Array.isArray(weeklySet?.items) ? weeklySet.items : [];
    const order = ["WEEKLY_PLAYER", "WEEKLY_FORWARD", "WEEKLY_MIDFIELDER", "WEEKLY_DEFENDER", "WEEKLY_GOALKEEPER", "WEEKLY_FAIR_PLAY"];
    return order.map((t) => items.find((i) => i.award_type === t)).filter((i) => i && (i.player_name || i.player_id));
  })();
  const weeklyCurrent = weeklyItems.length ? weeklyItems[weeklyIdx % weeklyItems.length] : null;
  const weeklyTeam = Array.isArray(weeklySet?.items) ? weeklySet.items.find((i) => i.award_type === "WEEKLY_TEAM") : null;
  const resultsList = finishedMatches.slice(0, 4);
  const fixturesList = fixtures.slice(0, 4);
  const cardsSeen = playerRows.reduce((acc, p) => ({ y: acc.y + (p.yellowCards || 0), r: acc.r + (p.redCards || 0) }), { y: 0, r: 0 });
  const playerTabLabels = { mvp: "En Değerli", goals: "Gol Krallığı", all: "En Çok Maç" };

  return (
    <div className={`alt-home-page hp-page ${themeClassName}`}>
      <SiteHeader {...headerProps} />

      <main className="hp">
        {/* ============ 1. MANŞET + SIRADAKİ MAÇ + GOL KRALLIĞI ============ */}
        <section className="hp-top">
          <div className="hp-hero" role="region" aria-label="Öne çıkan maçlar">
            {heroSlides.length === 0 ? (
              <div className="hp-slide is-active hp-slide--empty">
                <div className="hp-slide__bg" style={{ backgroundImage: `url(${defaultHeroImage})` }} />
                <div className="hp-slide__scrim" />
                <span className="hp-slide__stripes" aria-hidden="true" />
                <div className="hp-slide__content">
                  <p className="hp-slide__eyebrow">{scope.scopeLabel || "ELİTLİG"}</p>
                  <h1 className="hp-slide__headline">Lig merkezi</h1>
                  <span className="hp-slide__rule" aria-hidden="true" />
                  <p className="hp-slide__lead">Bu sezon için henüz maç planlanmadı.</p>
                </div>
              </div>
            ) : (
              <>
                {heroSlides.map((slide, idx) => renderSlide(slide, idx))}
                {heroSlides.length > 1 && (
                  <div className="hp-hero__dots" role="tablist" aria-label="Manşet seçimi">
                    {heroSlides.map((s, idx) => (
                      <button
                        key={`dot-${s.m.id}-${idx}`}
                        type="button"
                        role="tab"
                        aria-selected={idx === slideIdx}
                        className={idx === slideIdx ? "is-active" : ""}
                        onClick={() => setSlideIdx(idx)}
                        aria-label={`Manşet ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <aside className="hp-top__side">
            {spotlightMatch && (
              <Link to={matchLink(spotlightMatch.id)} className={`hp-card hp-next${spotlightIsLive ? " is-live" : ""}`}>
                <div className="hp-next__head">
                  <span className="hp-next__tab">
                    {spotlightIsLive ? <><span className="hp-live-dot" /> Canlı Maç</> : spotlightIsFinished ? <><Ico d={ICONS.ball} size={20} /> Son Maç</> : <><Ico d={ICONS.ball} size={20} /> Sıradaki Maç</>}
                  </span>
                  <span className="hp-next__when"><CalIcon /> {fmtDateShort(spotlightMatch.date)} · {fmtTime(spotlightMatch.time)}</span>
                </div>
                <div className="hp-next__teams">
                  <span className="hp-next__team">
                    <TeamMark name={spotlightMatch.first_team_name} logo={logoOf(spotlightMatch.first_team_id, spotlightMatch.first_team_name, spotlightMatch.first_team_logo)} size={52} />
                    <b>{spotlightMatch.first_team_name}</b>
                  </span>
                  <span className="hp-next__vs">
                    {(spotlightIsLive || spotlightIsFinished)
                      ? `${n(spotlightMatch.first_team_score) ?? 0} - ${n(spotlightMatch.second_team_score) ?? 0}`
                      : "VS"}
                  </span>
                  <span className="hp-next__team is-away">
                    <b>{spotlightMatch.second_team_name}</b>
                    <TeamMark name={spotlightMatch.second_team_name} logo={logoOf(spotlightMatch.second_team_id, spotlightMatch.second_team_name, spotlightMatch.second_team_logo)} size={52} />
                  </span>
                </div>
                {!spotlightIsLive && !spotlightIsFinished && <Countdown targetTs={getMatchTs(spotlightMatch)} />}
              </Link>
            )}

            <article className="hp-card hp-scorers">
              <header className="hp-card__head">
                <h2><Ico d={ICONS.target} /> Gol Krallığı</h2>
                <span>İlk {Math.min(5, topScorers.length) || 5} oyuncu</span>
              </header>
              {topScorers.length === 0 ? (
                <p className="hp-empty">Henüz gol kaydı yok.</p>
              ) : (
                <ol className="hp-scorers__list">
                  {topScorers.slice(0, 3).map((p, i) => (
                    <li key={p.id}>
                      <Link to={playerLink(p.id)} className="hp-scorer">
                        <span className="hp-rank">{i + 1}</span>
                        <span className="hp-avatar">{p.img ? <img src={p.img} alt="" loading="lazy" /> : <span>{p.name?.charAt(0) || "?"}</span>}</span>
                        <span className="hp-scorer__id"><b>{p.name}</b><small>{p.teamName || "Takımsız"}</small></span>
                        <span className="hp-badge">{p.goals}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              )}
              <button type="button" className="hp-card__more" onClick={toPlayersPage}>Tüm Gol Krallığı <ArrowIcon /></button>
            </article>
          </aside>
        </section>

        {/* ============ 2. SEZON SAYI ŞERİDİ ============ */}
        <section className="hp-strip" aria-label="Sezon özeti">
          <div className="hp-strip__items">
            <div className="hp-strip__item"><Ico d={ICONS.users} size={40} /><span><b>{leaguePulse?.teamsCount ?? standings.length ?? 0}</b><small>Takım</small></span></div>
            <div className="hp-strip__item"><Ico d={ICONS.pitch} size={40} /><span><b>{leaguePulse?.playedTotal ?? finishedMatches.length}</b><small>Oynanan Maç</small></span></div>
            <div className="hp-strip__item"><Ico d={ICONS.ball} size={40} /><span><b>{leaguePulse?.goalsTotal ?? 0}</b><small>Atılan Gol</small></span></div>
            {(cardsSeen.y > 0 || cardsSeen.r > 0) ? (
              <div className="hp-strip__item"><span className="hp-cards" aria-hidden="true"><i /><i /></span><span><b>{cardsSeen.y}/{cardsSeen.r}</b><small>Kart</small></span></div>
            ) : (
              <div className="hp-strip__item"><Ico d={ICONS.cal} size={40} /><span><b>{fixtures.length}</b><small>Planlanan Maç</small></span></div>
            )}
            {leaderTeam && (
              <Link to={leaderTeam.id != null ? teamLink(leaderTeam.id) : `${scopedPrefix}/puan-durumu`} className="hp-strip__item hp-strip__leader">
                <Ico d={ICONS.trophy} size={40} />
                <span><b>{leaderTeam.name}</b><small>Lider · {leaderTeam.points} Puan</small></span>
              </Link>
            )}
          </div>
          <button type="button" className="hp-strip__cta" onClick={toStandingsPage}>
            <Ico d={ICONS.chart} size={30} /> Sezon İstatistikleri <ArrowIcon />
          </button>
        </section>

        {/* ============ SPONSOR ============ */}
        <SiteSponsorBanner cityId={cityId || undefined} />

        {/* ============ CANLI MAÇLAR ============ */}
        {liveMatches.length > 0 && (
          <section className="hp-card hp-live">
            <header className="hp-card__head">
              <h2><span className="hp-live-dot" /> Canlı Maçlar</h2>
              <span className="hp-card__meta">{liveMatches.length} maç oynanıyor</span>
            </header>
            <ul className="lm-live-strip">
              {liveMatches.map((m) => (
                <LiveMatchCard
                  key={m.id}
                  match={m}
                  to={matchLink(m.id)}
                  homeLogo={logoOf(m.first_team_id, m.first_team_name, m.first_team_logo)}
                  awayLogo={logoOf(m.second_team_id, m.second_team_name, m.second_team_logo)}
                />
              ))}
            </ul>
          </section>
        )}

        {/* ============ 3. DÖRT KART ============ */}
        <section className="hp-quad">
          {/* SON SONUÇLAR */}
          <article className="hp-card">
            <header className="hp-card__head">
              <h2><span className="hp-ico"><Ico d={ICONS.ball} size={18} /></span> Son Sonuçlar</h2>
              <span className="hp-card__meta">{resultsList[0] ? fmtDayMonth(resultsList[0].date).label : ""}</span>
            </header>
            {resultsList.length === 0 ? <p className="hp-empty">Henüz sonuç yok.</p> : (
              <ul className="hp-rows">
                {resultsList.map((m) => {
                  const hs = n(m.first_team_score) ?? 0;
                  const as = n(m.second_team_score) ?? 0;
                  const when = fmtDayMonth(m.date);
                  return (
                    <li key={m.id}>
                      <Link to={matchLink(m.id)} className="hp-vrow" aria-label={`${m.first_team_name} ${hs} - ${as} ${m.second_team_name}`}>
                        <span className={`hp-vrow__team${hs < as ? " is-loss" : hs > as ? " is-win" : ""}`}>
                          <TeamMark name={m.first_team_name} logo={logoOf(m.first_team_id, m.first_team_name, m.first_team_logo)} size={30} />
                          <span className="hp-vrow__name">{m.first_team_name}</span>
                        </span>
                        <span className="hp-vrow__mid">
                          <span className="hp-score" aria-hidden="true">{hs} - {as}</span>
                          <small>{when.label}</small>
                        </span>
                        <span className={`hp-vrow__team${as < hs ? " is-loss" : as > hs ? " is-win" : ""}`}>
                          <TeamMark name={m.second_team_name} logo={logoOf(m.second_team_id, m.second_team_name, m.second_team_logo)} size={30} />
                          <span className="hp-vrow__name">{m.second_team_name}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            <button type="button" className="hp-card__more" onClick={toMatchesPage}>Tüm Sonuçlar <ArrowIcon /></button>
          </article>

          {/* HAFTANIN MAÇLARI */}
          <article className="hp-card">
            <header className="hp-card__head">
              <h2><span className="hp-ico"><Ico d={ICONS.cal} size={18} /></span> Haftanın Maçları</h2>
              <span className="hp-card__meta">{fixturesList.length ? `${fixtures.length} maç` : ""}</span>
            </header>
            {fixturesList.length === 0 ? <p className="hp-empty">Planlanmış maç yok.</p> : (
              <ul className="hp-rows">
                {fixturesList.map((m) => {
                  const when = fmtDayMonth(m.date);
                  const pitch = m.pitch_name || m.field_name || "";
                  return (
                    <li key={m.id}>
                      <Link to={matchLink(m.id)} className="hp-vrow" title={pitch || undefined}>
                        <span className="hp-vrow__team">
                          <TeamMark name={m.first_team_name} logo={logoOf(m.first_team_id, m.first_team_name, m.first_team_logo)} size={30} />
                          <span className="hp-vrow__name">{m.first_team_name}</span>
                        </span>
                        <span className="hp-vrow__mid">
                          <span className="hp-vrow__time">{fmtTime(m.time)}</span>
                          <small>{when.label}</small>
                        </span>
                        <span className="hp-vrow__team">
                          <TeamMark name={m.second_team_name} logo={logoOf(m.second_team_id, m.second_team_name, m.second_team_logo)} size={30} />
                          <span className="hp-vrow__name">{m.second_team_name}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            <button type="button" className="hp-card__more" onClick={toMatchesPage}>Tüm Fikstür <ArrowIcon /></button>
          </article>

          {/* PUAN DURUMU */}
          <article className="hp-card hp-table-card">
            <header className="hp-card__head">
              <h2><span className="hp-ico"><Ico d={ICONS.trophy} size={18} /></span> Puan Durumu</h2>
              <span className="hp-card__meta">İlk 5</span>
            </header>
            {standings.length === 0 ? <p className="hp-empty">Puan tablosu için sonuç bekleniyor.</p> : (
              <table className="hp-table">
                <thead>
                  <tr><th>#</th><th className="t-left">Takım</th><th>O</th><th className="t-opt">G</th><th className="t-opt">B</th><th className="t-opt">M</th><th>Av</th><th>P</th></tr>
                </thead>
                <tbody>
                  {standings.slice(0, 5).map((t, idx) => (
                    <tr key={t.key} className={`${idx === 0 ? "is-first" : ""}${t.id != null ? " is-link" : ""}`} onClick={t.id != null ? () => navigate(teamLink(t.id)) : undefined}>
                      <td><span className="hp-table__pos">{idx + 1}</span></td>
                      <td className="t-left"><span className="hp-table__team"><TeamMark name={t.name} logo={t.logo} size={22} /><span>{t.name}</span></span></td>
                      <td>{t.played}</td><td className="t-opt">{t.wins}</td><td className="t-opt">{t.draws}</td><td className="t-opt">{t.losses}</td>
                      <td>{t.goalDiff > 0 ? `+${t.goalDiff}` : t.goalDiff}</td>
                      <td className="hp-table__pts">{t.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button type="button" className="hp-card__more" onClick={toStandingsPage}>Tüm Puan Durumu <ArrowIcon /></button>
          </article>

          {/* HAFTANIN ENLERİ */}
          <article className="hp-card">
            <header className="hp-card__head">
              <h2><span className="hp-ico"><Ico d={ICONS.star} size={18} /></span> Haftanın Enleri</h2>
              <span className="hp-card__meta">{weeklySet?.start_date ? fmtDayMonth(weeklySet.start_date).label : ""}</span>
            </header>
            {weeklyCurrent ? (
              <div className="hp-weekly">
                <Link to={weeklyCurrent.player_id ? playerLink(weeklyCurrent.player_id) : "#"} className="hp-weekly__card">
                  <span className="hp-weekly__photo">
                    {weeklyCurrent.player_img ? <img src={weeklyCurrent.player_img} alt="" loading="lazy" /> : <span>{String(weeklyCurrent.player_name || "?").charAt(0)}</span>}
                  </span>
                  <span className="hp-weekly__text">
                    <small>{WEEKLY_LABELS[weeklyCurrent.award_type] || "Haftanın Enleri"}</small>
                    <b>{weeklyCurrent.player_name || `#${weeklyCurrent.player_id}`}</b>
                    <em>{weeklyCurrent.player_team_name || ""}</em>
                  </span>
                </Link>
                {weeklyItems.length > 1 && (
                  <div className="hp-weekly__dots" role="tablist" aria-label="Haftanın enleri">
                    {weeklyItems.map((it, i) => (
                      <button key={it.award_type} type="button" role="tab" aria-selected={i === weeklyIdx % weeklyItems.length} className={i === weeklyIdx % weeklyItems.length ? "is-active" : ""} onClick={() => setWeeklyIdx(i)} aria-label={WEEKLY_LABELS[it.award_type] || it.award_type} />
                    ))}
                  </div>
                )}
                {weeklyTeam && (
                  <Link to={weeklyTeam.team_id ? teamLink(weeklyTeam.team_id) : "#"} className="hp-weekly__team">
                    <TeamMark name={weeklyTeam.team_name} logo={weeklyTeam.team_logo} size={30} />
                    <span><small>Haftanın Takımı</small><b>{weeklyTeam.team_name}</b></span>
                  </Link>
                )}
              </div>
            ) : starPlayer ? (
              <div className="hp-weekly">
                <Link to={playerLink(starPlayer.id)} className="hp-weekly__card">
                  <span className="hp-weekly__photo">{starPlayer.img ? <img src={starPlayer.img} alt="" loading="lazy" /> : <span>{starPlayer.name?.charAt(0) || "?"}</span>}</span>
                  <span className="hp-weekly__text"><small>Sezonun Yıldızı</small><b>{starPlayer.name}</b><em>{starPlayer.teamName} · {starPlayer.points} puan</em></span>
                </Link>
              </div>
            ) : <p className="hp-empty">Bu hafta için ödül açıklanmadı.</p>}
            <button type="button" className="hp-card__more" onClick={toPlayersPage}>Tüm Haftanın Enleri <ArrowIcon /></button>
          </article>
        </section>

        {/* ============ 4. İSTATİSTİK LİDERLERİ + HAFTANIN KARMASI ============ */}
        <section className="hp-duo">
          <article className="hp-card hp-leaders-card">
            <header className="hp-card__head">
              <h2><span className="hp-ico"><Ico d={ICONS.chart} size={18} /></span> İstatistik Liderleri</h2>
              <div className="hp-seg" role="tablist" aria-label="Oyuncu sıralamaları">
                {["mvp", "goals", "all"].map((key) => (
                  <button key={key} type="button" role="tab" aria-selected={playerTab === key} className={playerTab === key ? "is-active" : ""} onClick={() => setPlayerTab(key)}>{playerTabLabels[key]}</button>
                ))}
              </div>
            </header>
            {playersLoading ? <p className="hp-empty">Oyuncular yükleniyor…</p> : playerRows.length === 0 ? <p className="hp-empty">Oyuncu bulunamadı.</p> : (
              <ol className="hp-leaders">
                {playerRows.slice(0, 8).map((p, i) => {
                  const value = playerTab === "mvp" ? p.points : playerTab === "goals" ? p.goals : p.matches;
                  return (
                    <li key={`${p.id}-${p.rank}`}>
                      <Link to={playerLink(p.id)} className={`hp-leader${i < 3 ? " is-podium" : ""}`}>
                        <span className="hp-leader__rank">{i + 1}</span>
                        <span className="hp-avatar">{p.img ? <img src={p.img} alt="" loading="lazy" /> : <span>{p.name?.charAt(0) || "?"}</span>}</span>
                        <span className="hp-leader__id"><b>{p.name}</b><small>{p.teamName || "Takımsız"} · {p.matches} maç · {p.goals} gol</small></span>
                        <span className="hp-leader__val"><b>{value}</b><small>{playerTabMeta[playerTab].unit}</small></span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            )}
            <button type="button" className="hp-card__more" onClick={toPlayersPage}>Tüm Oyuncular <ArrowIcon /></button>
          </article>

          <div className="hp-duo__side">
            {cityId && <WeeklyLineupCard set={weeklySet} playerLink={playerLink} />}
            {winStreakTeam && winStreakTeam.streak >= 2 && (
              <Link to={winStreakTeam.id != null ? teamLink(winStreakTeam.id) : "#"} className="hp-card hp-streak">
                <Ico d={ICONS.pulse} size={30} />
                <span><small>Galibiyet serisi</small><b>{winStreakTeam.name}</b></span>
                <strong>{winStreakTeam.streak}<small>maç</small></strong>
              </Link>
            )}
          </div>
        </section>

        {/* ============ 5. SEZON PANOSU ============ */}
        {insights.length > 0 && (
          <section className="hp-insights">
            {insights.map((card) => {
              const inner = (
                <>
                  <small>{card.label}</small>
                  <span className="hp-insights__team"><TeamMark name={card.team.name} logo={card.team.logo} size={34} /><b>{card.team.name}</b></span>
                  <em>{card.value}</em>
                </>
              );
              return card.team.id != null
                ? <Link key={card.key} to={teamLink(card.team.id)} className="hp-card hp-insight">{inner}</Link>
                : <div key={card.key} className="hp-card hp-insight">{inner}</div>;
            })}
          </section>
        )}

        {/* ============ 6. BİLGİLENDİRME AKIŞI ============ */}
        <section className="hp-feed">
          <HomeInfoFeed results={finishedMatches} matchLink={matchLink} cityId={cityId} />
        </section>
      </main>
    </div>
  );
}
